// Panel state keyed by the pog atom, surviving tab switches. The component's local `previewOn` resets to false on every remount by design, so the persisted flag is the only witness that a preview is applied.
// It lives for one studio session: Close, Finalize and the gate's Cancel end it, and the next
// open takes the plan on the atom as its original.
import type { Pog } from "@CommonTypes/merchflow/pog/pog";

/** A fix made on the plan: the plan before it, to go back to, and the draft step it was made at. */
export interface FixStep {
  pog: Pog;
  working: Pog;
  planBeforeFixes: Pog | null;
  undoDepth: number;
  /** The fixes the step made: one, or as many as Fix all keeps. */
  fixes: number;
}

/** The fixes standing on the plan, kept so a remount still offers Undo, Reset fixes and the
 *  Discard prompt for them. */
export interface StandingFixes {
  planBeforeFixes: Pog | null;
  fixSteps: readonly FixStep[];
  undoneFixes: readonly { step: FixStep; made: Pog }[];
}

export const NO_FIXES: StandingFixes = { planBeforeFixes: null, fixSteps: [], undoneFixes: [] };

export interface PanelPreviewState {
  originalPog: Pog;
  previewOn: boolean;
  fixes: StandingFixes;
}

const PANEL_STATE = new WeakMap<object, PanelPreviewState>();

export const getPanelPreviewState = (key: object): PanelPreviewState | undefined =>
  PANEL_STATE.get(key);

export const resetPanelPreviewState = (key: object, originalPog: Pog): PanelPreviewState => {
  const created: PanelPreviewState = { originalPog, previewOn: false, fixes: NO_FIXES };
  PANEL_STATE.set(key, created);
  return created;
};

export const initPanelPreviewState = (key: object, originalPog: Pog): PanelPreviewState =>
  PANEL_STATE.get(key) ?? resetPanelPreviewState(key, originalPog);

export const setPanelPreviewOn = (key: object, previewOn: boolean): void => {
  const entry = PANEL_STATE.get(key);
  if (entry) entry.previewOn = previewOn;
};

export const setPanelFixes = (key: object, fixes: StandingFixes): void => {
  const entry = PANEL_STATE.get(key);
  if (entry) entry.fixes = fixes;
};

/** The fixes a remount takes up: only those standing on a plan the studio laid. */
export const standingFixesOf = (key: object): StandingFixes => {
  const entry = PANEL_STATE.get(key);
  return entry?.previewOn ? entry.fixes : NO_FIXES;
};

/** Ends the session. Returns whether the original is owed back: when either the live or the
 *  persisted flag says the plan on the atom is the studio's. */
export const endPanelPreviewSession = (key: object, localPreviewOn: boolean): boolean => {
  const shouldRestore = localPreviewOn || PANEL_STATE.get(key)?.previewOn === true;
  PANEL_STATE.delete(key);
  return shouldRestore;
};
